/* ============================================================================
   Enshield Design DNA — INTELLIGENCE FIELD
   ----------------------------------------------------------------------------
   The atmosphere: a slow violet field that drifts behind the hero and the
   closing CTA. It says monitoring and automation without any of the vocabulary
   the brief rules out — no neon, no particles, no crypto shimmer. It is a
   weather system, not an effect.

   Written directly against WebGL rather than through three.js or ogl. A
   marketing background does not justify a 3D engine: this is roughly 3 KB of
   source against ~150 KB for the smallest sensible library, and the whole
   program is one fullscreen quad and one fragment shader.

   Cost control, in order of how much they save:

   1. Renders at half resolution and is scaled up by CSS. The output is a soft
      cloud, so there is nothing for the missing pixels to resolve — this
      quarters the fragment work and is invisible at any viewport.
   2. Device pixel ratio is capped, so a 3x phone does not pay 9x.
   3. The loop stops entirely when the element scrolls out of view or the tab
      is hidden, via IntersectionObserver and visibilitychange.
   4. Four octaves of value noise, not simplex — cheaper, and indistinguishable
      once blurred this far.

   It degrades to the CSS gradient underneath it, which is always present, in
   every one of these cases: prefers-reduced-motion, no WebGL context, shader
   compile failure, and WebGL context loss at runtime. The section is designed
   to look finished without it; the field is enrichment, never load-bearing.
   ========================================================================== */
import { useEffect, useRef, useState } from 'react';

const VERTEX = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAGMENT = `
precision mediump float;
uniform vec2  u_res;
uniform float u_time;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i),               hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0,1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.02; a *= 0.5; }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  vec2 p  = uv * vec2(2.1, 1.5);
  float t = u_time * 0.028;

  float n1 = fbm(p + vec2(t, t * 0.55));
  float n2 = fbm(p * 1.7 - vec2(t * 0.7, t * 0.35));

  // Enshield violet, and the lavender it fades toward.
  vec3 violet   = vec3(0.408, 0.282, 0.937);
  vec3 lavender = vec3(0.706, 0.647, 1.000);

  float m1 = smoothstep(0.34, 0.96, n1);
  float m2 = smoothstep(0.46, 1.00, n2);

  vec3 col = mix(lavender, violet, m2 * 0.62);

  // Hold the density around the upper right, where the hero artwork sits,
  // and fade to nothing at the edges so the band has no visible boundary.
  float d    = distance(uv, vec2(0.66, 0.60));
  float edge = 1.0 - smoothstep(0.18, 0.92, d);

  float alpha = (m1 * 0.52 + m2 * 0.30) * edge * 0.62;
  gl_FragColor = vec4(col, alpha);
}
`;

function compile(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

export function IntelligenceField({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Starts false: the CSS gradient shows until the field has actually painted,
  // so a failure at any stage simply leaves the static treatment in place.
  const [live, setLive] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    // Honour the OS setting before doing any GPU work at all.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (reduced?.matches) return undefined;

    const gl = (canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' })
      || canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    if (!gl) return undefined;

    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
    const program = vs && fs ? gl.createProgram() : null;
    if (!vs || !fs || !program) return undefined;

    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return undefined;

    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(program, 'u_res');
    const uTime = gl.getUniformLocation(program, 'u_time');

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Half resolution, DPR capped at 1.5. The result is a blurred cloud, so
    // the discarded detail has nothing to resolve.
    const SCALE = 0.5;
    let width = 0;
    let height = 0;
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr * SCALE));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr * SCALE));
      if (w === width && h === height) return;
      width = w;
      height = h;
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
    };

    let frame = 0;
    let running = false;
    let visible = true;
    let onScreen = true;
    const start = performance.now();

    const render = (now: number) => {
      resize();
      gl.uniform2f(uRes, width, height);
      gl.uniform1f(uTime, (now - start) / 1000);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      frame = requestAnimationFrame(render);
    };

    const play = () => {
      if (running || !visible || !onScreen) return;
      running = true;
      frame = requestAnimationFrame(render);
    };
    const pause = () => {
      running = false;
      cancelAnimationFrame(frame);
    };

    // Never animate a field nobody is looking at.
    const observer = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      if (onScreen) play();
      else pause();
    }, { threshold: 0 });
    observer.observe(canvas);

    const onVisibility = () => {
      visible = document.visibilityState === 'visible';
      if (visible) play();
      else pause();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // A lost context means the GPU took the surface away; fall back rather
    // than leave an empty canvas over the gradient.
    const onLost = (event: Event) => {
      event.preventDefault();
      pause();
      setLive(false);
    };
    canvas.addEventListener('webglcontextlost', onLost);

    setLive(true);
    play();

    return () => {
      pause();
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('webglcontextlost', onLost);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteBuffer(buffer);
      // Deliberately NOT calling WEBGL_lose_context.loseContext() here.
      // Forcing the loss is irreversible for that canvas element, and the
      // canvas outlives this cleanup in two real cases: StrictMode's
      // double-invoked effect in development, and any genuine remount such as
      // navigating away from Home and back. In both, the second run received
      // a dead context and silently painted nothing — the field looked like
      // it was working because the CSS layer underneath is always there.
      // Dropping the context is the browser's job once the element is gone.
    };
  }, []);

  return (
    <div className={['ifield', className].filter(Boolean).join(' ')} aria-hidden="true">
      {/* Always painted. The canvas layers over it when the field is live. */}
      <span className="ifield__static" />
      <canvas ref={canvasRef} className="ifield__canvas" data-live={live} />
    </div>
  );
}
